import axios from 'axios';

// 动态获取后端 API 地址
// 如果设置了 REACT_APP_API_URL 环境变量，使用它
// 否则，使用当前主机的地址（支持局域网访问）
const getBaseURL = () => {
  // 如果有环境变量配置，优先使用
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // 生产环境使用相对路径
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  
  // 开发环境：检测当前协议（http/https）并使用相同协议
  const protocol = window.location.protocol; // 'http:' or 'https:'
  const hostname = window.location.hostname;
  const port = 5001; // 后端端口
  
  // 如果通过 IP 或域名访问，使用相同的协议和主机
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    // 如果前端是 HTTPS，后端也应该是 HTTPS（或使用相对路径）
    // 但通常开发环境后端是 HTTP，所以需要特殊处理
    if (protocol === 'https:') {
      // HTTPS 前端访问 HTTP 后端会导致混合内容错误
      // 尝试使用相对路径或警告用户
      console.warn('[API] Frontend is HTTPS but backend is HTTP. This may cause SSL errors.');
      console.warn('[API] Consider using HTTP for frontend or HTTPS for backend.');
      // 对于开发环境，如果前端是 HTTPS，尝试使用 HTTP（可能被浏览器阻止）
      // 或者使用相对路径让代理处理
      return '/api'; // 使用相对路径，让开发服务器代理
    }
    return `${protocol}//${hostname}:${port}/api`;
  }
  
  // 默认本机开发 - 使用当前协议
  return `${protocol}//localhost:${port}/api`;
};

const instance = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
});

// Add auth token to requests
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Bypass ngrok warning page for API requests
    if (config.baseURL && config.baseURL.includes('ngrok')) {
      // ngrok 免费版需要添加这些头部来绕过警告页面
      config.headers['ngrok-skip-browser-warning'] = 'true';
      config.headers['X-Forwarded-Proto'] = 'https';
    }
    
    // Bypass localtunnel authentication page
    // This header tells localtunnel to skip the verification page for API requests
    if (config.baseURL && config.baseURL.includes('.loca.lt')) {
      config.headers['bypass-tunnel-reminder'] = 'true';
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors and network errors
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle SSL/network errors
    if (error.code === 'ERR_SSL_PROTOCOL_ERROR' || error.message?.includes('SSL') || error.message?.includes('protocol')) {
      console.error('[API] SSL Protocol Error detected. This usually happens when:');
      console.error('  1. Frontend is HTTPS but backend is HTTP (mixed content)');
      console.error('  2. SSL certificate issues');
      console.error('  3. Protocol mismatch');
      console.error(`Current URL: ${window.location.href}`);
      console.error(`API Base URL: ${instance.defaults.baseURL}`);
      
      // Show user-friendly error
      const userMessage = '网络连接错误：SSL 协议错误。请确保前端和后端使用相同的协议（HTTP 或 HTTPS）。';
      error.userMessage = userMessage;
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default instance;

